import EmbeddedForm from '@/components/Form/embeddedForm/EmbeddedForm';
import { InlineAlert, InlineAlertTitle } from '@/components/alert';
import CodeRevealer from '@/components/codeRevealer/CodeRevealer';
import { checkVorteil, getGenericCode } from '@/components/codeRevealer/actions/revealAction';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import LoginBox from '@/components/loginBox/LoginBox';
import { RichText } from '@/components/richText/RichText';
import { type VoucherPropsWithoutCode, vtwCreate } from '@/helper/microservices/vorteilsweltService';
import { type ReplacementsType, replacePlaceholders } from '@/helper/replacements';
import tracking from '@/helper/tracking';
import { getTokens } from '@/helper/vtwutils';
import OrderStepProvider from '@/providers/OrderStepContext';
import { useSettings } from '@/providers/SettingsProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import { useVtwSettings } from '@/providers/VtwSettingsProvider';
import type { PageVtwBenefitStoryblok } from '@/types/component-types-sb';
import { isComponentVtwVoucherGenericStoryblok, isFormStoryblok } from '@/types/typeguards';
import { type ErrorEntity, type ErrorState, ErrorType, type Vorteil } from '@/types/vorteilswelt';
import type { ISbStoryData } from '@storyblok/react/rsc';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type BenefitBoxProps = {
  story: ISbStoryData<PageVtwBenefitStoryblok>;
  replacements: ReplacementsType;
};

const BenefitBox: React.FC<BenefitBoxProps> = ({ story, replacements }) => {
  const blok = story.content;
  const benefit = blok.benefit[0];

  const benefitForm = benefit && isFormStoryblok(benefit) ? benefit : undefined;
  const isGenericVoucher = benefit && isComponentVtwVoucherGenericStoryblok(benefit);
  const benefitVoucherGeneric = isGenericVoucher ? benefit : undefined;

  const { t } = useTranslation();
  const settings = useSettings();
  const vtwSettings = useVtwSettings();

  const translations = {
    exclusiv: t('vtw.exclusive'),
    intro: t('vtw.intro'),
    benefitHeading: t('vtw.benefitHeading'),
    codeHeading: t('vtw.codeHeading'),
    boxInformation: t('vtw.information'),
    boxPartner: t('vtw.partner'),
    boxInstructions: t('vtw.instructions'),
  };

  const userStore = useUserStore((state) => ({
    loggedIn: state.loggedIn,
    name: state.name,
    trackingData: state.trackingData,
    businessPartnerNumber: state.businessPartnerNumber,
    getUserState: state.getUserState,
  }));

  const [voucherProps, setVoucherProps] = useState<VoucherPropsWithoutCode>();
  const [vorteil, setVorteil] = useState<Vorteil | undefined>();
  const [alreadyRedeemed, setAlreadyRedeemed] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState>(null);
  const [loading, setLoading] = useState(true);
  const [trackingBeginCheckout, setTrackingBeginCheckout] = useState(false);
  const [redemptionCheckCompleted, setRedemptionCheckCompleted] = useState(false);

  const errorMessages: { [key in ErrorType]: ErrorEntity } = {
    [ErrorType.UNAUTHORIZED]: {
      title: vtwSettings?.unauthorized_headline,
      text: vtwSettings?.unauthorized_text,
      severity: 'warning',
    },
    [ErrorType.EMPTY]: {
      title: vtwSettings?.empty_headline,
      text: vtwSettings?.empty_text,
      severity: 'error',
    },
    [ErrorType.OVER]: {
      title: vtwSettings?.over_headline,
      text: vtwSettings?.over_text,
      severity: 'error',
    },
    [ErrorType.UPCOMING]: {
      title: vtwSettings?.upcoming_headline,
      text: vtwSettings?.upcoming_text,
      severity: 'info',
    },
    [ErrorType.DEFAULT]: {
      title: vtwSettings?.default_headline,
      text: vtwSettings?.default_text,
      severity: 'error',
    },
  };

  useEffect(() => {
    if (userStore?.loggedIn === undefined) return;
    if (!userStore?.loggedIn) {
      setLoading(false);
      return;
    }
    if (voucherProps) {
      return;
    }
    setVoucherProps({
      name: `${userStore?.name?.firstname} ${userStore?.name?.lastname}`,
      vorteil: blok.headline,
      partner: JSON.parse(replacePlaceholders(replacements, JSON.stringify(blok.box_partner))),
      instructions: JSON.parse(replacePlaceholders(replacements, JSON.stringify(blok.box_instructions))),
      information: JSON.parse(replacePlaceholders(replacements, JSON.stringify(blok.box_information))),
      partnerLogo: blok.partner_logo,
      translations: translations,
      settings: settings,
      tokens: getTokens(),
    });
  }, [
    userStore?.loggedIn,
    userStore?.name,
    voucherProps,
    blok.headline,
    blok.box_partner,
    blok.box_instructions,
    blok.box_information,
    blok.partner_logo,
    replacements,
    settings,
    translations,
  ]);

  useEffect(() => {
    if (
      !benefitForm &&
      voucherProps &&
      userStore?.trackingData &&
      !trackingBeginCheckout &&
      !alreadyRedeemed &&
      redemptionCheckCompleted
    ) {
      tracking.beginCheckout([userStore.trackingData]);
      setTrackingBeginCheckout(true);
    }
  }, [
    voucherProps,
    userStore?.trackingData,
    trackingBeginCheckout,
    benefitForm,
    alreadyRedeemed,
    redemptionCheckCompleted,
  ]);

  async function getVorteil() {
    if (vorteil) {
      return;
    }
    if (!(userStore?.loggedIn && voucherProps && userStore?.businessPartnerNumber)) {
      return;
    }
    setLoading(true);
    const check = await checkVorteil(story.full_slug);
    setLoading(false);
    if (check.error) {
      setError(errorMessages[check.error]);
      setRedemptionCheckCompleted(true);
      return;
    }
    if (check.vorteil) {
      setAlreadyRedeemed(true);
      setVorteil(check.vorteil);
      setRedemptionCheckCompleted(true);
      return;
    }
    setRedemptionCheckCompleted(true);
    if (!benefitVoucherGeneric) {
      return;
    }
    setLoading(true);
    const res = await getGenericCode({
      voucherProps: voucherProps,
      slug: story.full_slug,
      genericCoupon: benefitVoucherGeneric.generic_coupon,
    });
    setLoading(false);
    if (res.error) {
      setError(errorMessages[res.error]);
      return;
    }
    if (res.vorteil) {
      setVorteil(res.vorteil);
      return;
    }
  }

  useEffect(() => {
    getVorteil();
  }, [voucherProps]);

  useEffect(() => {
    if (!vorteil) return;
    setLoading(false);
  }, [vorteil]);

  const finishFormAction = async () => {
    try {
      if (!(blok && userStore?.loggedIn && voucherProps)) {
        throw new Error('finishFormAction dependencies not loaded');
      }
      await vtwCreate(voucherProps, story.uuid, vorteil?.userCode);
    } catch (error) {
      console.error(error);
      setError(errorMessages.DEFAULT);
    }
  };

  const form = benefitForm && (
    <OrderStepProvider>
      <EmbeddedForm
        blok={benefitForm}
        embedded={true}
        onSuccess={finishFormAction}
        trackFormPurchase={true}
        formSourceStoryUuid={story.uuid}
      />
    </OrderStepProvider>
  );

  const successAlert = (
    <InlineAlert severity="success">
      <InlineAlertTitle>
        {replacePlaceholders(replacements, vtwSettings?.form_success_headline ?? t('Vielen Dank für Ihre Teilnahme'))}
      </InlineAlertTitle>
      <RichText text={vtwSettings?.form_success_text} replace={replacements} />
    </InlineAlert>
  );

  const errorAlert = (
    <InlineAlert variant="standard" severity={error?.severity ?? 'error'} data-testid="revealer-alert">
      <InlineAlertTitle>{replacePlaceholders(replacements, error?.title ?? t('Fehler'))}</InlineAlertTitle>
      <RichText text={error?.text} replace={replacements} />
    </InlineAlert>
  );

  if (error) {
    return errorAlert;
  }

  if (userStore?.loggedIn === undefined || loading) {
    return <LoadingBackdrop loading type="card" />;
  }

  if (!userStore?.loggedIn) {
    return (
      <LoginBox
        loggedInText={vtwSettings?.logged_in_text}
        loggedOutText={vtwSettings?.logged_out_text}
        replacements={replacements}
      />
    );
  }

  return (
    <>
      {benefitForm ? (
        <>
          {/* if form is already submitted, show success message; if user has not submitted the form yet, show form */}
          {vorteil ? successAlert : form}
        </>
      ) : (
        <CodeRevealer
          story={story}
          replacements={replacements}
          setError={(error: ErrorType) => {
            setError(errorMessages[error]);
          }}
          voucherProps={voucherProps}
          vorteil={vorteil}
          genericVoucherProps={benefitVoucherGeneric}
          alreadyRedeemed={alreadyRedeemed}
        />
      )}
    </>
  );
};

export default BenefitBox;
